import { ChatWidget } from "components/ChatWidget/ChatWidget";
import { LoadingSpinner } from "components/LoadingSpinner";
import {
  ED_ORDER_TYPE_EXPIRATION_PERIOD,
  orderTypeConstants,
} from "constants/order";
import { orderHsitoryTypeConstants } from "constants/orderHistory";
import { events } from "constants/tagManager";
import { useUserWithAccount } from "context/UserAccountProvider";
import { centsToDollars } from "helpers/price";
import { useAnalytics } from "hooks/useAnalytics";
import useDocumentTitle from "hooks/useDocumentTitle";
import usePrice from "hooks/usePrice";
import { useQuery } from "hooks/useQuery";
import { debounce, isEmpty } from "lodash";
import { DEFAULT_EXPIRATION_DAYS } from "pages/OrderOverview/utils/constants";
import { Button } from "primereact/button";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useGetCustomsByOrderTypeAndAccountIdQuery } from "store/queries/customize";
import { useGetPublicOrderQuery } from "store/queries/order";
import { useCreateOrderHistoryMutation } from "store/queries/orderHistory";
import { onChangeAccountInfo } from "store/slices/order";
import { TrainingTourHost } from "training/components/TrainingTourHost";
import { IOrder } from "types/Order/Order";
import { IProvider } from "types/Provider/Provider";

import { PaymentSuccessFull } from "../PaymentSection/PaymentSuccessFull/PaymentSuccessFull";
import { PayNowSection } from "../PayNowSection";
import { Home } from "./components/Home";
import PaymentPlan from "./components/PaymentSections/PaymentPlanSection";
import { groupByCodeDescriptionAndCount } from "./helpers";
import {
  StyledPageContainer,
  StyledBreadcrumbContainer,
  StyledBreadCrumb,
} from "./styled";

export function Overview() {
  useDocumentTitle("Estimate Overview");
  const query = useQuery();
  const { id: externalId } = useParams();
  const [createOrderHistory] = useCreateOrderHistoryMutation();
  const { userInfo } = useUserWithAccount();

  const internalUser = query.get("internalUser");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { getClearPriceByOrderType, getInsuranceAmounts, insuranceAmounts } =
    usePrice();
  const { pageViewEvents } = useAnalytics();

  const [disclaimerDialogVisible, setDisclaimerDialogVisible] = useState(false);

  const [showPaymentSection, setShowPaymentSection] = useState({
    paymentSection: false,
    paymentPlan: false,
    paymentSuccess: false,
    receiptAvailable: true,
  });

  const [showClearEstimateDetailsSection, setShowClearEstimateDetailsSection] =
    useState(false);

  const { data, isLoading, isError, refetch } = useGetPublicOrderQuery(
    externalId || ""
  );

  useEffect(() => {
    getInsuranceAmounts(data?.data.gfeQuote?.pricingBreakdown);
  }, [data?.data.gfeQuote?.pricingBreakdown]);

  const debouncedPageViewEvents = debounce((order, event: string) => {
    pageViewEvents(
      {
        accountName: order?.accountName,
        orderType: order?.orderType,
        communicationMethod: order?.communicationMethod,
      },
      event
    );
  }, 300);

  useEffect(() => {
    if (isEmpty(data)) return;

    debouncedPageViewEvents(
      {
        accountName: data?.data.account.name,
        orderType: data?.data.orderType,
        communicationMethod: data?.data.patient.preferredContactMethod,
      },
      events.CHECKOUT
    );
  }, [data]);

  useEffect(() => {
    if (internalUser) return;
    if (data) {
      createOrderHistory({
        type: orderHsitoryTypeConstants.EstimateOpened,
        orderId: data.data.id,
      });
    }
  }, [internalUser, data]);

  const orderInfo = useMemo(() => {
    const accountInfo = data?.data.account;

    if (accountInfo) {
      dispatch(onChangeAccountInfo({ accountInfo }));
    }

    return data?.data || ({} as IOrder);
  }, [data]);

  const { data: contentData, isFetching: isContentFetching } =
    useGetCustomsByOrderTypeAndAccountIdQuery(
      { orderType: orderInfo.orderType, accountId: orderInfo.accountId },
      {
        skip: !orderInfo.orderType,
      }
    );

  const { overviewPage: content } = contentData?.data || {};

  const prices = getClearPriceByOrderType({ order: orderInfo });
  const { percentageOff } = prices;

  const fullPrice = centsToDollars(prices.total);
  const clearPrice = centsToDollars(prices.clearPrice);
  const totalSaving = centsToDollars(prices.totalSaving);

  const physicianFee = centsToDollars(prices.physicianFee || 0);
  const facilityFee = centsToDollars(prices.facilityFee || 0);

  const getExpirationDays = () => {
    // if (isLoading) return DEFAULT_EXPIRATION_DAYS;

    if (
      orderInfo.orderType === orderTypeConstants.ED ||
      orderInfo.orderType === orderTypeConstants.BARIATRICS
    ) {
      return ED_ORDER_TYPE_EXPIRATION_PERIOD;
    }
    return 0;

    // return (orderInfo.account.providers[0] as unknown as IProvider)
    //   .expirationDays;
  };

  const expirationDays = getExpirationDays();

  const publicContact = () => {
    const currentProvider = orderInfo.account
      .providers[0] as unknown as IProvider;

    const isVisible =
      currentProvider.publicContactName &&
      currentProvider.publicContactRole &&
      currentProvider.publicContactPhone;

    return {
      isVisible,
      name: currentProvider.publicContactName,
      role: currentProvider.publicContactRole,
      phone: currentProvider.publicContactPhone,
    };
  };

  const showClearEstimateDetails = () => {
    setShowClearEstimateDetailsSection(!showClearEstimateDetailsSection);
  };

  const homeView = useMemo(() => {
    return (
      !showPaymentSection.paymentSection &&
      !showPaymentSection.paymentPlan &&
      !showPaymentSection.paymentSuccess
    );
  }, [showPaymentSection]);

  const breadcrumbItems = useMemo(() => {
    const itemsBase = {
      label: "Estimate",
      template: (
        <Button
          onClick={() => {
            navigate(`/orders/${orderInfo.id}`);
          }}
          className="p-button-text"
        >
          Estimate
        </Button>
      ),
    };

    const checkoutItem = {
      label: "Checkout",
      template: <span className="active">Checkout</span>,
    };
    return [
      ...(internalUser ? [itemsBase] : []),
      {
        label: "Billing Statement",
        template: homeView ? (
          <span className="active">Billing Statement</span>
        ) : (
          <Button
            onClick={() => {
              setShowPaymentSection({
                paymentSection: false,
                paymentPlan: false,
                paymentSuccess: false,
                receiptAvailable: true,
              });
            }}
            className="p-button-text"
          >
            Billing Statement
          </Button>
        ),
      },
      ...(homeView ? [] : [checkoutItem]),
    ];
  }, [internalUser, homeView, orderInfo]);

  const showBreadCrumb = useMemo(() => {
    if (!internalUser && homeView) {
      return false;
    }
    return true;
  }, [internalUser, homeView]);

  const {
    totalDeductibleInCents,
    totalCoinsuranceInCents,
    totalInsurancePayInCents,
    totalFlatCopayInCents,
    totalAllowedInsuranceAmount,
  } = insuranceAmounts;

  if (!orderInfo || isError) {
    return <Navigate to="/not-found" />;
  }

  // We can't use the useLoading hook because the clientSecret is required to load the payment for now.
  if (isLoading || isContentFetching) {
    return <LoadingSpinner />;
  }

  if (showPaymentSection.paymentSuccess && externalId) {
    return (
      <PaymentSuccessFull
        publicContact={publicContact}
        orderExternalId={externalId}
        receiptAvailable={showPaymentSection.receiptAvailable}
        orderUrl={
          internalUser && userInfo ? `/orders/${orderInfo.id}` : undefined
        }
      />
    );
  }

  // Check if account has a patient chat bot configured
  const shouldShowChatWidget = orderInfo?.account?.patientChatBotId;

  // Check if there's a chatId in the URL params to auto-open chat widget
  const chatIdFromUrl = query.get("chatId");
  const shouldAutoOpenChat = !!shouldShowChatWidget && !!chatIdFromUrl;

  return (
    <>
      {/* CLE-3015 — Module 4 at the desk: only a staff user who opened this page from Collect Payment. */}
      {internalUser && (
        <TrainingTourHost
          route="patient"
          accountId={orderInfo.accountId}
          orderExternalId={externalId}
        />
      )}
      <div className="flex flex-column w-full align-items-center">
        {showBreadCrumb && (
          <StyledBreadcrumbContainer className="px-3 pt-3">
            <StyledBreadCrumb model={breadcrumbItems} />
          </StyledBreadcrumbContainer>
        )}
        <StyledPageContainer
          maxWidth={
            showPaymentSection.paymentSection || showPaymentSection.paymentPlan
              ? "500px"
              : undefined
          }
          minWidth={
            showPaymentSection.paymentSection || showPaymentSection.paymentPlan
              ? "500px"
              : undefined
          }
        >
          {showPaymentSection.paymentSection && (
            <PayNowSection
              {...{
                expirationDays,
                orderInfo,
                showPaymentSection,
                setShowPaymentSection,
                onReturnClick: () => {
                  setShowPaymentSection({
                    paymentPlan: false,
                    paymentSection: false,
                    paymentSuccess: false,
                    receiptAvailable: false,
                  });
                },
                clearPrice,
                totalSaving,
                fullPrice,
                payRemainingPrice: false,
              }}
            />
          )}
          {showPaymentSection.paymentPlan && (
            <PaymentPlan
              {...{
                expirationDays,
                orderInfo,
                showPaymentSection,
                setShowPaymentSection,
                clearPrice,
                totalSaving,
                fullPrice,
                payRemainingPrice: false,
              }}
            />
          )}
          {homeView && (
            <Home
              orderInfo={orderInfo}
              content={content}
              showPaymentSection={showPaymentSection}
              setShowPaymentSection={setShowPaymentSection}
              expirationDays={expirationDays}
              physicianFee={physicianFee}
              facilityFee={facilityFee}
              totalDeductibleInCents={totalDeductibleInCents}
              totalCoinsuranceInCents={totalCoinsuranceInCents}
              totalInsurancePayInCents={totalInsurancePayInCents}
              totalFlatCopayInCents={totalFlatCopayInCents}
              totalAllowedInsuranceAmount={totalAllowedInsuranceAmount}
              publicContact={publicContact}
              showClearEstimateDetails={showClearEstimateDetails}
              showClearEstimateDetailsSection={showClearEstimateDetailsSection}
              procedures={groupByCodeDescriptionAndCount(
                orderInfo.serviceDetails.procedures
              )}
              isGFE={orderInfo.orderType === orderTypeConstants.GFE}
              clearPrice={clearPrice}
              totalSaving={totalSaving}
              fullPrice={fullPrice}
              percentageOff={percentageOff}
              setDisclaimerDialogVisible={setDisclaimerDialogVisible}
              disclaimerDialogVisible={disclaimerDialogVisible}
              refetchOrder={refetch}
              isInternalUser={!!internalUser}
            />
          )}
        </StyledPageContainer>
      </div>
      {shouldShowChatWidget && (
        <ChatWidget
          accountId={orderInfo.accountId}
          chatType="PatientChatBot"
          contextData={{ orderExternalId: externalId || "" }}
          patientFirstName={orderInfo?.patient?.firstName}
          shouldAutoOpen={shouldAutoOpenChat}
          position="fixed"
        />
      )}
    </>
  );
}
